import _ from 'lodash';
import { HourMode } from 'views/Analytics/AnalyticsMockService/Reporting/Pickers/BzmDateRangePicker/BzmTimeRangePicker';
import moment from 'moment';

/**
 * Generates date in format for blank objects.
 */
export const generateDate = (): string => {
  const datetime = new Date();
  const month = datetime.toLocaleString('en-us', { month: 'short' });
  const day = datetime.getDate();
  const time = datetime.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
  });

  return `${month}_${day}_${time}`;
};

/**
 * Formats seconds to date asset catalog date format.
 * @param seconds
 */
export const formatDateTimeFromSeconds = (seconds: number, showSeconds?: boolean): string => {
  const datetime = new Date(seconds * 1000);
  return formatDateAndTime(datetime, showSeconds);
};

export const formatDateTimeFromMillis = (milliseconds: number): string => {
  const datetime = new Date(milliseconds);
  return formatDate(datetime) + ' ' + formatTime(datetime, true, true);
};

/**
 * Formats date object to the following format: ${day}/${month}/${year}
 * @param date
 */
export const formatDate = (datetime: Date): string => {
  let day: string | number = datetime.getDate();
  const year = datetime.getFullYear();
  const shortMonth = _.padStart(String(datetime.getMonth() + 1), 2, '0');
  day = _.padStart(String(day), 2, '0');
  return `${shortMonth}/${day}/${year}`;
};

/**
 * Formats date object to the following format: ${monthName}/${day}${time}
 * @param date
 */
export const formatDateTime = (datetime: Date): string => {
  let day: string | number = datetime.getDate();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  day = _.padStart(String(day), 2, '0');
  const time = datetime.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
  });
  return `${monthNames[datetime.getMonth()]}_${day}_${time}`;
};

/**
 * Format time to US format (12 hours version)
 * @param datetime date object to be formatted
 * @param showSeconds if set to true, seconds will be shown in the time string
 * @param showMillis if set to true, milliseconds will be shown in the time string
 */
export const formatTime = (datetime: Date, showSeconds?: boolean, showMillis?: boolean): string => {
  let formattedDate = datetime.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
  });

  // AM / PM mode
  const mode = formattedDate.substring(formattedDate.length - 3);
  if (showSeconds === true) {
    if (showMillis === true) {
      const milliseconds = datetime.getMilliseconds();
      formattedDate = formattedDate.substring(0, formattedDate.length - 3);
      return `${formattedDate}.${milliseconds}${mode}`;
    }

    return formattedDate;
  }

  formattedDate = formattedDate.substring(0, formattedDate.length - 6) + mode;
  return formattedDate;
};

/**
 * Formats date object to ui format representation.
 * @param datetime date object to be formatted
 * @param showSeconds if false, seconds will not be shown in the time string
 */
export const formatDateAndTime = (datetime: Date, showSeconds?: boolean): string => {
  return formatDate(datetime) + ' ' + formatTime(datetime, showSeconds);
};

/**
 * Formats an ISO timestamp as a short "MMM YYYY" string in UTC.
 * Example: "2025-09-15T12:00:00Z" → "Sep 2025"
 */
export const shortMonthYear = (iso: string): string => {
  const date = new Date(iso);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
};

/**
 * Compares calendar dates
 * @param firstDay first date
 * @param secondDay second date
 */
export const isSameDate = (firstDay: Date, secondDay: Date) => {
  const start = new Date(firstDay).setHours(0, 0, 0, 0);
  const end = new Date(secondDay).setHours(0, 0, 0, 0);
  return start === end;
};

export const bzmUTCformat = (date: Date) => {
  if (date === null) {
    return null;
  }
  return (
    date
      .toISOString()
      .replace(/-/g, '/')
      .toString()
      .substring(0, 10) +
    ' ' +
    getUTCTimeStringFromDate(date)
  );
};

/**
 * Removes millis from ISO string. This format needs to be used for Java Backend.
 * @param dateIsoString date in ISO format
 */
export const removeMillisFromISOString = (dateIsoString: string) => {
  return dateIsoString.substring(0, dateIsoString.length - 5) + 'Z'; //remove mileseconds;
};

export const roundInUTCString = (date: Date | null, hours: number, minutes: number) => {
  if (date === null) {
    return null;
  }
  const dateUTCString = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), hours, minutes, 0),
  ).toISOString();
  return removeMillisFromISOString(dateUTCString);
};

/**
 * It shifts the provided date by client time zone difference.
 * If client timezone is +60minutes from UTC time, the shift of the * provided date will be -60 minutes.
 * @param date
 */
export const shiftToUTCTimeZone = (date: Date | null) => {
  if (date === null) {
    return null;
  }

  // Get current client offset in minutes
  const browserTimezoneOffset = new Date().getTimezoneOffset(); //In minutes
  console.log('Current offset', browserTimezoneOffset);

  // Move the current date by this offset
  return new Date(date.getTime() + browserTimezoneOffset * 60 * 1000);
};

export const getUTCHoursFromDate = (date: Date): number => {
  let hours = date.getUTCHours();
  hours = hours % 12;
  hours = hours ? hours : 12;
  return hours;
};

export const getHoursFromDate = (date: Date): number => {
  let hours = date.getHours();
  hours = hours % 12;
  hours = hours ? hours : 12;
  return hours;
};

export const getModeFromDate = (date: Date): HourMode => {
  const hours = date.getHours();
  const mode = hours >= 12 ? HourMode.PM : HourMode.AM;
  return mode;
};

export const getUTCModeFromDate = (date: Date): HourMode => {
  const hours = date.getUTCHours();
  const mode = hours >= 12 ? HourMode.PM : HourMode.AM;
  return mode;
};

export const getUTCTimeStringFromDate = (date: Date) => {
  return (
    getUTCHoursFromDate(date) +
    ':' +
    _.padStart((date as Date).getUTCMinutes().toString(), 2, '0') +
    ' ' +
    getUTCModeFromDate(date)
  );
};

export const getTimeStringFromDate = (date: Date) => {
  return (
    getHoursFromDate(date) +
    ':' +
    _.padStart((date as Date).getMinutes().toString(), 2, '0') +
    ' ' +
    getModeFromDate(date)
  );
};

export const dateAndAuthorFormat = (itemDate: any | undefined, author: string | undefined): string => {
  const dateFormatted = getDateWithFormat(itemDate);
  return dateFormatted + ' by ' + (author ? author : '-');
};

export const getDateWithFormat = (itemDate: any): string => {
  let dateFormatted = '-';
  if (!_.isNil(itemDate)) {
    // previously serialized OffsetDateTime value.toEpochSecond()
    // multiplied by 1000 so value is in milliseconds, not seconds.
    itemDate = _.isNumber(itemDate) ? itemDate * 1000 : itemDate;
    dateFormatted = moment(itemDate).format('MMM D, YYYY, hh:mm A');
  }
  return dateFormatted;
};
//
// export const getDateWithoutMils = (itemDate: any) => {
//   itemDate = _.isNumber(itemDate) ? itemDate * 1000 : itemDate;
//   const date = new Date(itemDate);
//   date.setMilliseconds(0); // Set milliseconds to zero
//   return date;
// };

export const getDaysRemainingPermanentDelete = (itemDate: any): number => {
  const currentDate = new Date();
  // multiplied by 1000, so value is in milliseconds, not seconds.
  itemDate = _.isNumber(itemDate) ? itemDate * 1000 : itemDate;
  const inputDate = new Date(itemDate);

  const timeDifference = currentDate.getTime() - inputDate.getTime();
  const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  return 30 - daysDifference;
};
